/**
 * Selector-derived view models (GAM-9): pure functions from `(GameState, Content)`
 * to the overlay/scene shapes the ui and render layers consume. This is the seam
 * the demo economy used to fill — every number shown on screen is now computed by
 * `@core` selectors over the loaded content pack, and every icon is keyed by the
 * entity's content id (served from `/content/<id>.png`).
 */
import {
  TICKS_PER_SEC,
  breakthroughChance,
  breakthroughCost,
  cmp,
  cumulativeGeneratorCost,
  div,
  evalCurve,
  generatorCost,
  generatorMultiplier,
  getBreakthrough,
  getGenerator,
  getResource,
  gt,
  gte,
  isUnlocked,
  isZero,
  maxAffordable,
  mul,
  resourceContributors,
  resourceRatePerSec,
  toNumber,
  type Content,
  type GameState,
  type Notation,
  type NumSource,
} from '@core/index.js';
import type { Age } from '@content/index.js';
import {
  formatAmount,
  formatSignedAmount,
  type BreakthroughView,
  type GeneratorView,
  type GeneratorYieldView,
  type OverlayViewModel,
  type ResourceContributorView,
  type ResourceView,
  type UpgradeView,
} from '@ui/index.js';
import type { AgeSceneViewModel } from '@render/index.js';
import { contentArtUrl } from './contentArt.js';

/** The age currently being played, or `undefined` before one is selected. */
export function currentAge(state: GameState, content: Content): Age | undefined {
  return state.ages.current === null ? undefined : content.ages.get(state.ages.current);
}

/** The age's headline resource: what its click action grants (the big counter). */
export function primaryResourceId(age: Age): string | undefined {
  return age.clickAction.grants[0]?.resource;
}

const EMPTY_OVERLAY_VM: OverlayViewModel = {
  ticks: 0,
  resources: [],
  generators: [],
  upgrades: [],
  breakthroughs: [],
};

export interface OverlayVmOptions {
  readonly notation: Notation;
  /** Interpolated display value for the primary resource (smooth 60 fps counter). */
  readonly displayPrimary: number;
}

/**
 * Derive the DOM overlay's view model for the age being played. Visibility is
 * unlock-gated exactly like the engine's command validation: locked generators,
 * upgrades and breakthroughs simply don't render (owned upgrades stay visible).
 * Resources appear once they've ever been produced; the primary always shows.
 */
export function buildOverlayViewModel(
  state: GameState,
  content: Content,
  options: OverlayVmOptions,
): OverlayViewModel {
  const age = currentAge(state, content);
  if (age === undefined) return { ...EMPTY_OVERLAY_VM, ticks: state.meta.ticks };
  const { notation } = options;
  const primaryId = primaryResourceId(age);

  const resources: ResourceView[] = [];
  for (const [id, def] of content.resources) {
    if (def.age !== age.id) continue;
    const primary = id === primaryId;
    const owned = getResource(state, id);
    if (!primary && isZero(owned.lifetime)) continue;
    const rawContributors = resourceContributors(state, content, id);
    const totalContribution = rawContributors.reduce(
      (sum, c) => sum + Math.abs(toNumber(c.netPerSec)),
      0,
    );
    const contributors: ResourceContributorView[] = [...rawContributors]
      // Most to least, by share of the resource's total activity — the point
      // of the breakdown is "what's driving this", so the biggest driver leads.
      .sort((a, b) => Math.abs(toNumber(b.netPerSec)) - Math.abs(toNumber(a.netPerSec)))
      .map((c) => ({
        label: content.generators.get(c.generatorId)?.name ?? c.generatorId,
        artUrl: contentArtUrl(c.generatorId),
        perTickDisplay: formatSignedAmount(div(c.netPerSec, TICKS_PER_SEC), notation),
        sharePctDisplay:
          totalContribution > 0
            ? `${String(Math.round((Math.abs(toNumber(c.netPerSec)) / totalContribution) * 100))}%`
            : '0%',
      }));
    const ratePerSec = resourceRatePerSec(state, content, id);
    resources.push({
      id,
      label: def.name,
      description: def.description ?? '',
      display: formatAmount(primary ? options.displayPrimary : owned.amount, notation),
      rateDisplay: formatSignedAmount(ratePerSec, notation),
      totalPerTickDisplay: formatSignedAmount(div(ratePerSec, TICKS_PER_SEC), notation),
      primary,
      artUrl: contentArtUrl(id),
      contributors,
    });
  }

  const generators: GeneratorView[] = [];
  for (const [id, def] of content.generators) {
    if (def.age !== age.id) continue;
    if (!isUnlocked(state, content, def)) continue;
    const budget = getResource(state, def.cost.resource).amount;
    const next = generatorCost(state, content, id);
    const tenCost = cumulativeGeneratorCost(state, content, id, 10);
    const maxCount = maxAffordable(state, content, id);
    // With nothing affordable, show the next single unit's cost as the target.
    const maxCost =
      maxCount > 0 ? cumulativeGeneratorCost(state, content, id, maxCount) : next.amount;
    // Per-owned-unit rate for the info popover — the multiplier applies
    // uniformly per unit, so this is the rate one more unit would add.
    const multiplier = generatorMultiplier(state, content, id);
    const yieldView = (
      y: { resource: string; baseRatePerSec: NumSource },
      sign: 1 | -1,
    ): GeneratorYieldView => ({
      resourceLabel: content.resources.get(y.resource)?.name ?? y.resource,
      resourceArtUrl: contentArtUrl(y.resource),
      ratePerSecDisplay: formatSignedAmount(
        mul(mul(y.baseRatePerSec, multiplier), sign),
        notation,
      ),
    });
    generators.push({
      id,
      label: def.name,
      description: def.description ?? '',
      owned: getGenerator(state, id).owned,
      costResourceArtUrl: contentArtUrl(next.resource),
      costResourceLabel: content.resources.get(next.resource)?.name ?? next.resource,
      buy1: {
        count: 1,
        costDisplay: formatAmount(next.amount, notation),
        affordable: gte(budget, next.amount),
      },
      buy10: {
        count: 10,
        costDisplay: formatAmount(tenCost, notation),
        affordable: gte(budget, tenCost),
      },
      buyMax: {
        count: maxCount,
        costDisplay: formatAmount(maxCost, notation),
        affordable: maxCount > 0,
      },
      produces: def.produces.map((y) => yieldView(y, 1)),
      consumes: (def.consumes ?? []).map((y) => yieldView(y, -1)),
      artUrl: contentArtUrl(id),
    });
  }

  const rankedUpgrades: { view: UpgradeView; owned: boolean; amount: NumSource }[] = [];
  for (const [id, def] of content.upgrades) {
    if (def.age !== age.id) continue;
    const owned = state.upgrades.includes(id);
    if (!owned && !isUnlocked(state, content, def)) continue;
    const amount = evalCurve(def.cost.curve, 0);
    rankedUpgrades.push({
      owned,
      amount,
      view: {
        id,
        label: def.name,
        description: def.description ?? '',
        costDisplay: formatAmount(amount, notation),
        costResourceArtUrl: contentArtUrl(def.cost.resource),
        costResourceLabel:
          content.resources.get(def.cost.resource)?.name ?? def.cost.resource,
        owned,
        canAfford: gte(getResource(state, def.cost.resource).amount, amount),
        artUrl: contentArtUrl(id),
      },
    });
  }
  // Not-owned first (cheapest to priciest), owned last — so the next thing worth
  // buying always leads the list instead of getting buried among owned entries.
  const upgrades = rankedUpgrades
    .sort((a, b) => {
      if (a.owned !== b.owned) return a.owned ? 1 : -1;
      return cmp(a.amount, b.amount);
    })
    .map((r) => r.view);

  const breakthroughs: BreakthroughView[] = [];
  for (const [id, def] of content.breakthroughs) {
    if (def.age !== age.id) continue;
    if (!isUnlocked(state, content, def)) continue;
    const costs = breakthroughCost(state, content, id);
    breakthroughs.push({
      id,
      label: def.name ?? id,
      description: def.description ?? '',
      chancePct: breakthroughChance(state, content, id) * 100,
      // The pity bar fills as banked pity closes the gap to a guaranteed hit.
      pity: getBreakthrough(state, id).pity,
      pityMax: Math.max(0, 1 - def.baseChance),
      canAttempt: costs.every((c) =>
        gte(getResource(state, c.resource).amount, c.amount),
      ),
      costs: costs.map((c) => ({
        resourceArtUrl: contentArtUrl(c.resource),
        resourceLabel: content.resources.get(c.resource)?.name ?? c.resource,
        amountDisplay: formatAmount(c.amount, notation),
      })),
      artUrl: contentArtUrl(id),
    });
  }

  return {
    ticks: state.meta.ticks,
    resources,
    generators,
    upgrades,
    breakthroughs,
  };
}

/**
 * The Pixi scene's view model: the age's primary quantity as a `log10` proxy
 * (tech-design §4 — magnitudes, never raw counts).
 */
export function buildSceneViewModel(
  state: GameState,
  content: Content,
): AgeSceneViewModel {
  const age = currentAge(state, content);
  const primaryId = age === undefined ? undefined : primaryResourceId(age);
  let logMagnitude = 0;
  if (primaryId !== undefined) {
    const lifetime = getResource(state, primaryId).lifetime;
    if (gt(lifetime, 0)) {
      const asNumber = toNumber(lifetime);
      // Past number range, break_infinity reads Infinity — clamp to its decade.
      logMagnitude = Number.isFinite(asNumber) ? Math.log10(asNumber + 1) : 308;
    }
  }
  return { ticks: state.meta.ticks, logMagnitude };
}
